import type { Outfit } from '../types';

// === Hair catalog: 6 sheets × 8 styles × 6 colors = 288 hairs ===
// 모두 풀캐릭터 PNG (빡빡이 + 헤어가 한 이미지에 통합) — public/images/character/full/<id>.png

type SheetKey = 'classic' | 'trendy' | 'cute' | 'glam' | 'edgy' | 'fantasy';

interface SheetSpec {
  key: SheetKey;
  label: string;
  styles: string[];      // 8 styles
  styleLabels: string[]; // 8 ko labels
  colors: string[];      // 6 colors
  colorLabels: string[]; // 6 ko labels
  colorHex: string[];    // 6 hex
  rarity: Outfit['rarity'];
}

// 6 colors used across most sheets (rows)
const STD_COLORS = ['black', 'brown', 'blonde', 'pink', 'mint', 'silver'];
const STD_COLOR_LABELS = ['🖤 검정', '🤎 갈색', '💛 금발', '🩷 핑크', '💚 민트', '🤍 은빛'];
const STD_COLOR_HEX = ['#1a1a1a', '#a47148', '#ffd95c', '#ff6fa1', '#7feedb', '#c8c8d8'];

const FX_COLORS = ['rainbow', 'galaxy', 'fire', 'ice', 'gold', 'neon'];
const FX_COLOR_LABELS = ['🌈 무지개', '✨ 갤럭시', '🔥 불꽃', '❄️ 얼음', '✨ 골드', '💫 네온'];
const FX_COLOR_HEX = ['#ff5050', '#1a0f3a', '#ff6022', '#7feedb', '#ffd700', '#ff00ff'];

const SHEETS: SheetSpec[] = [
  {
    key: 'classic',
    label: '클래식',
    styles: ['long_straight', 'long_wavy', 'twintails', 'ponytail', 'bob', 'buns', 'braids', 'pixie'],
    styleLabels: ['롱 스트레이트', '롱 웨이브', '양갈래', '포니테일', '단발', '헤어번', '땋은머리', '픽시컷'],
    colors: STD_COLORS, colorLabels: STD_COLOR_LABELS, colorHex: STD_COLOR_HEX,
    rarity: 'common',
  },
  {
    key: 'trendy',
    label: '트렌디',
    styles: ['side_swept', 'curtain_bangs', 'see_through', 'no_bangs', 'baby_bangs', 'half_up', 'hime_cut', 'lob'],
    styleLabels: ['사이드 스웹', '커튼 앞머리', '시스루뱅', '노 뱅', '베이비뱅', '반묶음', '히메컷', '롱 보브'],
    colors: STD_COLORS, colorLabels: STD_COLOR_LABELS, colorHex: STD_COLOR_HEX,
    rarity: 'common',
  },
  {
    key: 'cute',
    label: '큐트',
    styles: ['heart_buns', 'tiny_pigtails', 'curly_twintails', 'cat_buns', 'fluffy_bob', 'crown_braid', 'ribbon_pigtails', 'bow_bob'],
    styleLabels: ['하트 헤어번', '미니 양갈래', '컬리 양갈래', '캣 헤어번', '뽀글 단발', '크라운 땋기', '리본 양갈래', '리본 단발'],
    colors: STD_COLORS, colorLabels: STD_COLOR_LABELS, colorHex: STD_COLOR_HEX,
    rarity: 'rare',
  },
  {
    key: 'glam',
    label: '글래머',
    styles: ['princess_curls', 'mermaid_wavy', 'fishtail_braid', 'crown_braid_loose', 'hollywood_waves', 'beach_waves', 'ringlets', 'siren_waves'],
    styleLabels: ['공주 컬', '인어 웨이브', '피쉬테일', '크라운 루즈', '할리우드 웨이브', '비치 웨이브', '링글릿', '사이렌 웨이브'],
    colors: STD_COLORS, colorLabels: STD_COLOR_LABELS, colorHex: STD_COLOR_HEX,
    rarity: 'rare',
  },
  {
    key: 'edgy',
    label: '엣지',
    styles: ['asym_bob', 'undercut_bob', 'shag_mullet', 'wolf_cut', 'choppy_mullet', 'sleek_blunt', 'korean_layered', 'reverse_bob'],
    styleLabels: ['비대칭 보브', '언더컷 보브', '셰그 멀렛', '울프컷', '쇼츠 멀렛', '슬릭 블런트', '레이어드 롱', '리버스 보브'],
    colors: STD_COLORS, colorLabels: STD_COLOR_LABELS, colorHex: STD_COLOR_HEX,
    rarity: 'epic',
  },
  {
    key: 'fantasy',
    label: '판타지',
    styles: ['long_straight', 'long_wavy', 'twintails', 'ponytail', 'bob', 'buns', 'braids', 'pixie'],
    styleLabels: ['롱 스트레이트', '롱 웨이브', '양갈래', '포니테일', '단발', '헤어번', '땋은머리', '픽시컷'],
    colors: FX_COLORS, colorLabels: FX_COLOR_LABELS, colorHex: FX_COLOR_HEX,
    rarity: 'legend',
  },
];

function buildHairCatalog(): Outfit[] {
  const items: Outfit[] = [];
  for (const sheet of SHEETS) {
    for (let si = 0; si < sheet.styles.length; si++) {
      for (let ci = 0; ci < sheet.colors.length; ci++) {
        const id = `hair_${sheet.key}_${sheet.styles[si]}_${sheet.colors[ci]}`;
        const name = `${sheet.colorLabels[ci]} ${sheet.styleLabels[si]}`;
        const isStarter = sheet.key === 'classic' && sheet.styles[si] === 'long_straight' && sheet.colors[ci] === 'black';
        const cost = sheet.rarity === 'common' ? { coins: 200 }
                   : sheet.rarity === 'rare' ? { coins: 500 }
                   : sheet.rarity === 'epic' ? { coins: 1500 }
                   : { gems: 5 }; // legend
        items.push({
          id, slot: 'hair', name, rarity: sheet.rarity,
          themes: ['casual'], color: sheet.colorHex[ci],
          unlockMethod: isStarter ? 'starter' : 'shop',
          ...(isStarter ? {} : { cost }),
        });
      }
    }
  }
  return items;
}

export const OUTFITS: Outfit[] = buildHairCatalog();
export const OUTFITS_BY_ID: Record<string, Outfit> = Object.fromEntries(OUTFITS.map(o => [o.id, o]));
export const OUTFITS_BY_SLOT: Record<string, Outfit[]> = OUTFITS.reduce((acc, o) => {
  (acc[o.slot] ||= []).push(o);
  return acc;
}, {} as Record<string, Outfit[]>);

// 헤어 ID → 시트 메타 (BootScene 로딩 + 풀캐릭터 키 생성용)
export const HAIR_SHEETS = SHEETS;
export const HAIR_FULL_KEY = (id: string): string => `char_full_${id}`;

import { SEASON_OUTFITS } from './seasons';
SEASON_OUTFITS.forEach(o => {
  if (!OUTFITS_BY_ID[o.id]) {
    OUTFITS.push(o);
    OUTFITS_BY_ID[o.id] = o;
    (OUTFITS_BY_SLOT[o.slot] ||= []).push(o);
  }
});
